import React from 'react';
import { Box, Typography, Paper, Link, Alert } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const commonReferenceUrl = 'https://ij.org/issues/economic-liberty/homemade-food-seller/'
// State cottage food regulation resources
const STATE_REGULATIONS = {
  'AL': { name: 'Alabama', url: `${commonReferenceUrl}alabama/` },
  'AK': { name: 'Alaska', url: `${commonReferenceUrl}alaska/` },
  'AZ': { name: 'Arizona', url: `${commonReferenceUrl}arizona/` },
  'AR': { name: 'Arkansas', url: `${commonReferenceUrl}arkansas/` },
  'CA': { name: 'California', url: `${commonReferenceUrl}california/` },
  'CO': { name: 'Colorado', url: `${commonReferenceUrl}colorado/` },
  'CT': { name: 'Connecticut', url: `${commonReferenceUrl}connecticut/` },
  'DE': { name: 'Delaware', url: `${commonReferenceUrl}delaware/` },
  'FL': { name: 'Florida', url: `${commonReferenceUrl}florida/` },
  'GA': { name: 'Georgia', url: `${commonReferenceUrl}georgia/` },
  'HI': { name: 'Hawaii', url: `${commonReferenceUrl}hawaii/` },
  'ID': { name: 'Idaho', url: `${commonReferenceUrl}idaho/` },
  'IL': { name: 'Illinois', url: `${commonReferenceUrl}illinois/` },
  'IN': { name: 'Indiana', url: `${commonReferenceUrl}indiana/` },
  'IA': { name: 'Iowa', url: `${commonReferenceUrl}iowa/` },
  'KS': { name: 'Kansas', url: `${commonReferenceUrl}kansas/` },
  'KY': { name: 'Kentucky', url: `${commonReferenceUrl}kentucky/` },
  'LA': { name: 'Louisiana', url: `${commonReferenceUrl}louisiana/` },
  'ME': { name: 'Maine', url: `${commonReferenceUrl}maine/` },
  'MD': { name: 'Maryland', url: `${commonReferenceUrl}maryland/` },
  'MA': { name: 'Massachusetts', url: `${commonReferenceUrl}massachusetts/` },
  'MI': { name: 'Michigan', url: `${commonReferenceUrl}michigan/` },
  'MN': { name: 'Minnesota', url: `${commonReferenceUrl}minnesota/` },
  'MS': { name: 'Mississippi', url: `${commonReferenceUrl}mississippi/` },
  'MO': { name: 'Missouri', url: `${commonReferenceUrl}missouri/` },
  'MT': { name: 'Montana', url: `${commonReferenceUrl}montana/` },
  'NE': { name: 'Nebraska', url: `${commonReferenceUrl}nebraska/` },
  'NV': { name: 'Nevada', url: `${commonReferenceUrl}nevada/` },
  'NH': { name: 'New Hampshire', url: `${commonReferenceUrl}new-hampshire/` },
  'NJ': { name: 'New Jersey', url: `${commonReferenceUrl}new-jersey/` },
  'NM': { name: 'New Mexico', url: `${commonReferenceUrl}new-mexico/` },
  'NY': { name: 'New York', url: `${commonReferenceUrl}new-york/` },
  'NC': { name: 'North Carolina', url: `${commonReferenceUrl}north-carolina/` },
  'ND': { name: 'North Dakota', url: `${commonReferenceUrl}north-dakota/` },
  'OH': { name: 'Ohio', url: `${commonReferenceUrl}ohio/` },
  'OK': { name: 'Oklahoma', url: `${commonReferenceUrl}oklahoma/` },
  'OR': { name: 'Oregon', url: `${commonReferenceUrl}oregon/` },
  'PA': { name: 'Pennsylvania', url: `${commonReferenceUrl}pennsylvania/` },
  'RI': { name: 'Rhode Island', url: `${commonReferenceUrl}rhode-island/` },
  'SC': { name: 'South Carolina', url: `${commonReferenceUrl}south-carolina/` },
  'SD': { name: 'South Dakota', url: `${commonReferenceUrl}south-dakota/` },
  'TN': { name: 'Tennessee', url: `${commonReferenceUrl}tennessee/` },
  'TX': { name: 'Texas', url: `${commonReferenceUrl}texas/` },
  'UT': { name: 'Utah', url: `${commonReferenceUrl}utah/` },
  'VT': { name: 'Vermont', url: `${commonReferenceUrl}vermont/` },
  'VA': { name: 'Virginia', url: `${commonReferenceUrl}virginia/` },
  'WA': { name: 'Washington', url: `${commonReferenceUrl}washington/` },
  'WV': { name: 'West Virginia', url: `${commonReferenceUrl}west-virginia/` },
  'WI': { name: 'Wisconsin', url: `${commonReferenceUrl}wisconsin/` },
  'WY': { name: 'Wyoming', url: `${commonReferenceUrl}wyoming/` }
};

export default function StateRegulationReferences(props) {
  const kitchenState = props.kitchenData?.state;
  const regulation = kitchenState ? STATE_REGULATIONS[kitchenState] : null;

  if (!kitchenState) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 3, mb: 2 }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 0 }}>
          <strong>Compliance is Critical</strong>
        </Typography>
      </Alert>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 1, textAlign: 'justify' }}>
          Each state has unique cottage food laws that govern what you can sell, how much you can earn, 
          labeling requirements, and where you can sell your products. Review your state's regulations carefully 
          to ensure full compliance.
        </Typography>
      </Box>

      {regulation ? (
        <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Official Resources:
          </Typography>
          <Link 
            href={regulation.url} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <Typography variant="body1" sx={{ mr: 1 }}>
              {regulation.name} Cottage Food Laws
            </Typography>
            <OpenInNewIcon fontSize="small" />
          </Link>
        </Box>
      ) : (
        <Alert severity="info">
          Regulation information for {kitchenState} is not available. 
          Please contact your state's Department of Agriculture for cottage food guidelines.
        </Alert>
      )}

      <Box sx={{ mt: 1, p: 2, bgcolor: '#e0f7ff', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'info.dark', textAlign: 'justify' }}>
          <strong>Note:</strong> This information is provided as a reference only. Always verify current 
          regulations directly with your state's Department of Agriculture or Health Department, as laws 
          may change. Consider consulting with a legal professional or food safety expert for specific 
          guidance regarding your cottage food operation.
        </Typography>
      </Box>
    </Paper>
  );
}
